import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  inject,
  Input,
  Output,
  signal,
} from '@angular/core';
import {
  provideTranslocoScope,
  TranslocoModule,
  TranslocoService,
} from '@jsverse/transloco';
import { MzicSvgComponent, SpinnerComponent } from '@mzic/mzic-components';

@Component({
  selector: 'lib-share-info-dialog',
  imports: [CommonModule, TranslocoModule, MzicSvgComponent, SpinnerComponent],
  providers: [
    provideTranslocoScope({
      scope: 'mzic-collection-flow',
      alias: 'lang',
    }),
  ],
  templateUrl: './share-info-dialog.component.html',
  styleUrl: './share-info-dialog.component.scss',
})
export class ShareInfoDialogComponent {
  private readonly translocoService = inject(TranslocoService);

  @Input() mode: 'create' | 'info' = 'create';
  @Input() shareUrl = '';
  @Input() passcode = '';
  @Input() expiresAt = '';
  @Input() shareId = '';
  @Input() revoking = false;
  @Input() integrationEnabled = false;
  @Input() integrationEntityName = '';
  @Output() closeRequested = new EventEmitter<void>();
  @Output() revoke = new EventEmitter<string>();

  urlCopied = signal(false);
  passcodeCopied = signal(false);

  get integrationSuffix(): string {
    return this.integrationEnabled ? '_integration' : '';
  }

  get shareInfoSubtitleKey(): string {
    return `lang.share_info_subtitle${this.integrationSuffix}`;
  }

  get shareInfoSubtitleViewKey(): string {
    return `lang.share_info_subtitle_view${this.integrationSuffix}`;
  }

  get formattedExpiresAt(): string {
    if (!this.expiresAt) return '';
    const date = new Date(this.expiresAt);
    const locale =
      this.translocoService.getActiveLang()?.toLowerCase() === 'pt-br'
        ? 'pt-BR'
        : 'en-US';
    return date.toLocaleString(locale, {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  async copyUrl(): Promise<void> {
    await this.copyToClipboard(this.shareUrl);
    this.urlCopied.set(true);
    setTimeout(() => this.urlCopied.set(false), 2000);
  }

  async copyPasscode(): Promise<void> {
    await this.copyToClipboard(this.passcode);
    this.passcodeCopied.set(true);
    setTimeout(() => this.passcodeCopied.set(false), 2000);
  }

  private async copyToClipboard(text: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // Fallback for older browsers
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
    }
  }

  onClose(): void {
    this.closeRequested.emit();
  }

  onRevoke(): void {
    if (!this.shareId || this.revoking) return;
    this.revoke.emit(this.shareId);
  }
}
